import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { BuildingModel } from 'src/app/core/models/building.model';
import { BuildingService } from '../../building.service';
import { FormModalBaseComponent } from 'src/app/core/base/components/form-modal-base/form-modal-base.component';
import { AuthService } from '../../../../../../core/services/auth.service';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { GenericBuildingService } from '../../../../settings/generic-building/generic-building.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { GenericBuildingModel } from '../../../../../../core/models/generic-building.model';
import { ObjectApiInterface } from '../../../../../../core/base/interfaces/object-api.interface';
import { GenericBuildingCategoryService } from '../../../../settings/generic-building-category/generic-building-category.service';
import { GenericBuildingCategoryModel } from '../../../../../../core/models/generic-building-category.model';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { ResponseTypeEnum } from '../../../../../../core/base/enum/response-type.enum';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { MetrikTypeHoldingService } from '../../../../settings/metrik-type-holding/metrik-type-holding.service';
import { MetrikTypeHoldingModel } from '../../../../../../core/models/metrik-type-holding.model';
import { SupplierService } from '../../../supplier/supplier.service';
import { SupplierModel } from '../../../../../../core/models/supplier.model';
import { LeaseTypeService } from '../../../../settings/lease-type/lease-type.service';
import { LeaseTypeModel } from '../../../../../../core/models/lease-type.model';
import { UserProfileService } from '../../../user-profile/user-profile.service';
import { UserProfileModel } from '../../../../../../core/models/user-profile.model';
import { DefaultSupervisionService } from '../../../../../../core/services/default-supervision.service';
import {
  DefaultSupervisionContact,
  DefaultSupervisionMetrikTypeEnum,
  DefaultSupervisionResolvedLine,
} from '../../../../../../core/models/default-supervision.model';
import {
  selectSupervisionLine,
  SupervisionEstablishmentRef,
  supervisionProvenanceLabel,
  toSupervisionResolvePayload,
} from '../../../../../../core/utils/default-supervision.util';

@Component({
  selector: 'vex-building-create-update',
  templateUrl: './building-create-update-delete.component.html',
  styleUrls: ['./building-create-update-delete.component.scss'],
})
export class BuildingCreateUpdateDeleteComponent extends FormModalBaseComponent<BuildingModel> implements OnInit {
  private readonly metrikType = DefaultSupervisionMetrikTypeEnum.BUILDING;
  canDownloadTemplateModel = false;
  genericBuildings$: Observable<GenericBuildingModel[]>;
  genericBuildingCategory$: Observable<GenericBuildingCategoryModel[]>;
  detentionType$: Observable<MetrikTypeHoldingModel[]>;
  suppliers$: Observable<SupplierModel[]>;
  leaseTypes$: Observable<LeaseTypeModel[]>;
  contacts$: Observable<UserProfileModel[]>;
  /** Flags du type de détention sélectionné, toujours relus depuis le core (jamais depuis le bâtiment). */
  showSupplierField = false;
  showLeaseTypeField = false;
  currentEstablishment: EstablishmentModel | undefined;
  loading = false;
  /** Vrai tant que le superviseur affiché provient de la résolution du défaut (et non d'une saisie manuelle). */
  isSupervisorInherited = false;
  /** Libellé de provenance affiché sous le champ quand le superviseur est hérité du défaut. */
  inheritedSupervisorLabel?: string;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { defaults: BuildingModel; establishment?: EstablishmentModel; mode: CrudModeEnum; name?: string },
    public dialogRef: MatDialogRef<FormModalBaseComponent<BuildingModel>>,
    public service: BuildingService,
    public authService: AuthService,
    private genericBuildingService: GenericBuildingService,
    private genericBuildingCategoryService: GenericBuildingCategoryService,
    private metrikTypeHoldingService: MetrikTypeHoldingService,
    private supplierService: SupplierService,
    private leaseTypeService: LeaseTypeService,
    private userProfileService: UserProfileService,
    private defaultSupervisionService: DefaultSupervisionService,
  ) {
    super(data, dialogRef, service);
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.isUpdatingFromEstablishment()) {
      this.currentEstablishment = this.defaults.establishments?.find(e => e._id === this.data.establishment?._id);
    } else if (this.isCreatingFromEstablishment()) {
      this.currentEstablishment = this.data.establishment;
    }

    const formGroupConfig: { [key: string]: UntypedFormControl } = {
      _id: new UntypedFormControl(this.defaults?._id),
      establishment: new UntypedFormControl({ value: this.currentEstablishment?.label, disabled: true }),
      genericBuilding: new UntypedFormControl(this.defaults?.genericBuilding, [Validators.required]),
      genericBuildingCategory: new UntypedFormControl(this.defaults?.genericBuildingCategory, [Validators.required]),
      label: new UntypedFormControl(this.defaults?.label, [Validators.required]),
      customerDesignation: new UntypedFormControl(this.defaults?.customerDesignation),
      constructionDate: new UntypedFormControl(this.defaults?.constructionDate),
      surface: new UntypedFormControl(this.defaults?.surface, [Validators.required]),
      detentionType: new UntypedFormControl(this.defaults?.detentionType),
      supplier: new UntypedFormControl(this.defaults?.supplier),
      leaseType: new UntypedFormControl(this.defaults?.leaseType),
      contactIntern: new UntypedFormControl(this.defaults?.contactIntern),
      lastRegulatoryMaintenanceDate: new UntypedFormControl(this.defaults?.lastRegulatoryMaintenanceDate),
      lastMaintenanceDateOther: new UntypedFormControl(this.defaults?.lastMaintenanceDateOther),
      // La surface utilisée n'est pertinente (et requise) que lorsqu'on édite/crée le bâtiment
      usedSurface: new UntypedFormControl(
        this.currentEstablishment?.surface,
        this.isUpdatingFromEstablishment() || this.isCreatingFromEstablishment()
          ? [Validators.required, this.usedSurfaceMaxValidator]
          : [],
      ),
    };
    this.form = new UntypedFormGroup(formGroupConfig);

    const surfaceSub = this.form.controls.surface.valueChanges.subscribe(() => {
      this.form.controls.usedSurface.updateValueAndValidity();
    });
    this.subscription.add(surfaceSub);
    this.form.controls.usedSurface.updateValueAndValidity();

    this.authService.getCurrentUserPermissions$().subscribe(permissions => {
      this.canDownloadTemplateModel = permissions.includes(ENUM_PERMISSIONS.READ_BUILDING);
    });

    this._initData();
  }

  getEstablishmentsName() {
    return this.currentEstablishment?.label;
  }

  isUpdatingFromEstablishment(): boolean {
    return this.mode === CrudModeEnum.Update && !!this.data.establishment;
  }

  isCreatingFromEstablishment(): boolean {
    return this.mode === CrudModeEnum.Create && !!this.data.establishment;
  }

  _initData() {
    this.genericBuildingsCategoryAutocompleteChange('');
    this.genericBuildingsAutocompleteChange('');
    this.detentionTypeAutocompleteChange('');
    this.supplierAutocompleteChange('');
    this.leaseTypeAutocompleteChange('');
    this.contactInternAutocompleteChange('');
    this.refreshDetentionDependentFields();
    this.resolveDefaultSupervision();
  }

  beforeCreateItem() {
    this.normalizeContactInternForSave();
  }

  beforeUpdateItem() {
    if (this.isUpdatingFromEstablishment()) {
      const surface = this.form.controls.usedSurface.value;
      this.defaults = {
        ...this.defaults,
        usedSurface: {
          _id: this.currentEstablishment?._id,
          surface,
        },
      } as any;
    }
    this.normalizeContactInternForSave();
  }

  /**
   * Normalise `contactIntern` avant persistance. Seul un superviseur réellement choisi par
   * l'utilisateur est enregistré. Une suggestion héritée non validée OU un champ vidé (croix)
   * ne doivent pas écrire un contact :
   * - en création, on n'envoie rien (clé retirée) → le bâtiment résout son défaut dynamiquement ;
   * - en édition, on envoie `null` pour désaffecter explicitement le superviseur précédemment saisi.
   */
  private normalizeContactInternForSave(): void {
    const hasChosenContact = !this.isSupervisorInherited && !!this.form.controls.contactIntern.value?._id;
    if (hasChosenContact) {
      return;
    }

    if (this.mode === CrudModeEnum.Create) {
      delete (this.defaults as any).contactIntern;
    } else {
      this.defaults = { ...this.defaults, contactIntern: null } as any;
    }
  }

  updateItem(): void {
    this.loading = true;
    this.defaults = this.form.getRawValue();
    this.beforeUpdateItem();
    if (this.form.valid) {
      const sub = this.service.update(this.defaults).subscribe(
        result => {
          this.afterUpdateItem(result, null);
          this.setSnackbar(ResponseTypeEnum.Success, CrudModeEnum.Update, result.message);
        },
        error => {
          this.afterUpdateItem(null, error);
          this.setErrorsMessages(error.error.errors);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
    } else {
      this.logFormErrors(this.form); // Log des erreurs du formulaire
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }

  afterCreateItem(result?: any, error?: any) {
    if (!result) {
      return;
    }

    const building: BuildingModel = result.data;

    // Création depuis un établissement : on rattache automatiquement le bâtiment créé à celui-ci
    // (la création seule ne lie aucun établissement), avec la surface utilisée saisie.
    if (this.isCreatingFromEstablishment() && building?._id && this.currentEstablishment?._id) {
      const usedSurface = this.form.controls.usedSurface.value;
      const sub = this.service.addEstablishment(building._id, this.currentEstablishment._id, usedSurface).subscribe({
        next: () => this.close(building),
        error: err => {
          this.setErrorsMessages(err?.error?.errors);
          this.loading = false;
        },
      });
      this.subscription.add(sub);
      return;
    }

    this.close(building);
  }

  afterUpdateItem(result?: any, error?: any) {
    if (result) {
      this.close(result.data);
    }
  }

  afterImportItem(result?: any, error?: any) {
    if (result) {
      this.close(true);
    }
  }

  genericBuildingsCategoryAutocompleteChange(state: any) {
    this.genericBuildingCategory$ = this.genericBuildingCategoryService
      .findAll(null, null, 'label', 'asc', state, { isActive: true, isArchive: false, isDelete: false })
      .pipe(map((x: ObjectApiInterface<GenericBuildingCategoryModel[]>) => x.data));
  }

  onGenericBuildingCategoryChange(): void {
    this.form.controls.genericBuilding.reset();
    this.genericBuildingsAutocompleteChange('');
  }

  genericBuildingsAutocompleteChange(state: any) {
    const categoryId = this.form?.controls.genericBuildingCategory?.value?._id ?? null;
    // Tri explicite : passer `null` produit `sort=null@null` côté API, donc un ordre arbitraire
    // qui peut reléguer un bâtiment générique récent hors de la première page. Cf. PRED-3229.
    this.genericBuildings$ = this.genericBuildingService
      .findAll(null, null, 'label', 'asc', state, categoryId ? { buildingCategoryId: categoryId } : null)
      .pipe(map((x: ObjectApiInterface<GenericBuildingModel[]>) => x.data));
  }

  onGenericBuildingChange(): void {
    const genericBuilding = this.form.controls.genericBuilding.value;
    const categoryControl = this.form.controls.genericBuildingCategory;

    if (!genericBuilding) {
      categoryControl.enable();
      categoryControl.reset();
      this.genericBuildingsAutocompleteChange('');
      this.resolveDefaultSupervision();
      return;
    }
    categoryControl.setValue(genericBuilding.buildingCategory ?? null);
    categoryControl.disable();
    this.resolveDefaultSupervision();
  }

  detentionTypeAutocompleteChange(state: any) {
    this.detentionType$ = this.metrikTypeHoldingService
      .findAll(null, null, null, null, state, { isActive: true, isArchive: false, isDelete: false })
      .pipe(map((x: ObjectApiInterface<MetrikTypeHoldingModel[]>) => x.data));
  }

  supplierAutocompleteChange(state: any = '') {
    this.suppliers$ = this.supplierService
      .findAll(null, null, 'label', 'asc', state)
      .pipe(map((x: ObjectApiInterface<SupplierModel[]>) => x.data));
  }

  leaseTypeAutocompleteChange(state: any = '') {
    this.leaseTypes$ = this.leaseTypeService
      .findAll(null, null, 'label', 'asc', state, { isActive: true, isArchive: false, isDelete: false })
      .pipe(map((x: ObjectApiInterface<LeaseTypeModel[]>) => x.data));
  }

  onDetentionTypeChange(): void {
    this.refreshDetentionDependentFields();
  }

  /**
   * Affiche/masque les champs Fournisseurs et Type bail selon les flags du type de détention
   * sélectionné. Les flags sont toujours relus depuis le core : le document bâtiment ne les
   * stocke pas (la relation `detentionType` du bâtiment n'a que _id/label/technicalIdentifier),
   * et une valeur du dropdown peut avoir été rechargée entre-temps. Un champ masqué est vidé
   * pour ne pas persister une valeur invisible.
   */
  private refreshDetentionDependentFields(): void {
    const detentionTypeId = this.form?.controls.detentionType?.value?._id;

    if (!detentionTypeId) {
      this.applyDetentionFlags(false, false);
      return;
    }

    const sub = this.metrikTypeHoldingService.findById(detentionTypeId).subscribe({
      next: (response: ObjectApiInterface<MetrikTypeHoldingModel>) => {
        this.applyDetentionFlags(!!response.data?.hasAssociatedSupplier, !!response.data?.hasLeaseType);
      },
      error: () => this.applyDetentionFlags(false, false),
    });
    this.subscription.add(sub);
  }

  private applyDetentionFlags(showSupplier: boolean, showLeaseType: boolean): void {
    this.showSupplierField = showSupplier;
    this.showLeaseTypeField = showLeaseType;

    if (!showSupplier && this.form?.controls.supplier?.value) {
      this.form.controls.supplier.setValue(null);
    }
    if (!showLeaseType && this.form?.controls.leaseType?.value) {
      this.form.controls.leaseType.setValue(null);
    }
  }

  contactInternAutocompleteChange($event = '') {
    this.contacts$ = this.userProfileService
      // a building can be part of several establishments, so we do not filter with establishment like how equipments do.
      .findAll(null, null, null, null, $event, { isSupervision: true })
      .pipe(map(response => response.data.map(y => ({ ...y, fullName: `${y.firstName} ${y.lastName}` }))));
  }

  /**
   * Résout le superviseur « Supervision métier » par défaut le plus proche (établissement > tenant)
   * pour le bâtiment générique sélectionné et le pré-remplit dans le champ avec un libellé de
   * provenance. Un contact saisi manuellement par l'utilisateur n'est jamais écrasé.
   */
  resolveDefaultSupervision(): void {
    // Un superviseur saisi manuellement (édition d'un bâtiment existant ou choix utilisateur) prime
    // toujours : on n'effectue aucune résolution et on masque le libellé d'héritage.
    if (this.hasManualContact) {
      this.clearInheritedSupervisor();
      return;
    }

    const genericNodeId = this.form?.controls.genericBuilding?.value?._id;
    if (!genericNodeId) {
      // Plus de bâtiment générique : on retire la suggestion héritée précédemment posée.
      if (this.isSupervisorInherited) {
        this.form.controls.contactIntern.setValue(null);
      }
      this.clearInheritedSupervisor();
      return;
    }

    const establishments = this.resolveEstablishmentsForSupervision();
    const sub = this.defaultSupervisionService
      .resolveLines(this.metrikType, genericNodeId, toSupervisionResolvePayload(establishments))
      .pipe(map(response => response.data ?? []))
      .subscribe({
        next: lines => this.applyResolvedSupervisor(lines, establishments),
        error: () => this.clearInheritedSupervisor(),
      });
    this.subscription.add(sub);
  }

  /** Réinitialise l'état d'héritage dès que l'utilisateur choisit lui-même un superviseur. */
  onContactInternChange(): void {
    this.clearInheritedSupervisor();
  }

  /** Vrai quand le champ contient un superviseur choisi par l'utilisateur (et non une suggestion héritée). */
  get hasManualContact(): boolean {
    return !!this.form?.controls.contactIntern?.value?._id && !this.isSupervisorInherited;
  }

  /**
   * Sélectionne la ligne résolue selon la règle de restitution des fiches (PRED-3236 :
   * établissement gestionnaire, sinon premier établissement rattaché), pré-remplit le
   * superviseur et calcule le libellé de provenance. La fiche et le formulaire affichent
   * ainsi toujours le même superviseur.
   */
  private applyResolvedSupervisor(
    lines: DefaultSupervisionResolvedLine[],
    establishments: SupervisionEstablishmentRef[],
  ): void {
    const line = selectSupervisionLine(lines, establishments);
    if (!line?.contact) {
      // Aucun défaut couvrant ce bâtiment : on retire toute suggestion héritée précédente.
      if (this.isSupervisorInherited) {
        this.form.controls.contactIntern.setValue(null);
      }
      this.clearInheritedSupervisor();
      return;
    }

    const contact = line.contact;
    this.isSupervisorInherited = true;
    this.inheritedSupervisorLabel = this.buildInheritedLabel(line);
    this.form.controls.contactIntern.setValue({
      _id: contact._id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      fullName: this.contactFullName(contact),
    });
  }

  private buildInheritedLabel(line: DefaultSupervisionResolvedLine): string {
    const label = supervisionProvenanceLabel(line.source, this.metrikType);
    return line.source === 'establishment' && line.establishment?.label
      ? `${label} — ${line.establishment.label}`
      : label;
  }

  private contactFullName(contact: DefaultSupervisionContact): string {
    return `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  }

  private clearInheritedSupervisor(): void {
    this.isSupervisorInherited = false;
    this.inheritedSupervisorLabel = undefined;
  }

  /**
   * Établissements de la résolution, dans l'ordre de rattachement et avec le flag
   * `isMain` (établissement gestionnaire) qui pilote la ligne retenue. Vide = résolution
   * au niveau de l'organisation.
   */
  private resolveEstablishmentsForSupervision(): SupervisionEstablishmentRef[] {
    if (this.currentEstablishment?._id) {
      return [{ _id: this.currentEstablishment._id, label: this.currentEstablishment.label ?? '' }];
    }
    return (this.defaults?.establishments ?? []).map(e => ({
      _id: e._id,
      label: e.label ?? '',
      isMain: e.isMain === true,
    }));
  }

  // Validator croisé : la surface utilisée ne peut pas dépasser la surface totale du bâtiment.
  private readonly usedSurfaceMaxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const surface = control.parent?.get('surface')?.value;
    const usedSurface = control.value;

    // Tant qu'une des deux valeurs manque, on ne bloque pas (le champ `required` s'en charge).
    if (surface == null || surface === '' || usedSurface == null || usedSurface === '') {
      return null;
    }

    // Erreur `max` standard → affichée automatiquement par base-form-input-errors.
    return Number(usedSurface) <= Number(surface) ? null : { max: { max: surface, actual: usedSurface } };
  };
}
